import { Injectable, inject, signal, computed, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Observable, catchError, throwError, tap } from 'rxjs';
import { ApiService } from './api-base.service';
import { RolesService } from '../roles.service';
import { SocketService } from '../socket/socket.service';
import { PushNotificationService } from '../pwa/push-notification.service';
import {
    INotificationResponse,
    INotificationPaginationResponse,
    ICreateNotificationDto,
    IUpdateNotificationDto,
    INotificationFilters,
    INotificationSummary,
    INotificationStats,
    INotificationSettings,
    IBulkNotificationAction,
    INotificationTemplate,
    NotificationType,
    NotificationPriority,
} from '../../interfaces';
import { NOTIFICATION_ENDPOINTS, NOTIFICATION_CACHE_KEYS } from '../../constants';

/**
 * Serviço para gerenciamento de notificações
 * Seguindo as regras do projeto: métodos em português, signals do Angular
 * Migrado para usar ApiService centralizado com cache e loading states
 * Integrado com SocketService para notificações em tempo real
 */
@Injectable({ providedIn: 'root' })
export class NotificationService implements OnInit, OnDestroy {
    private readonly apiService = inject(ApiService);
    private readonly rolesService = inject(RolesService);
    private readonly socketService = inject(SocketService);
    private readonly pushNotificationService = inject(PushNotificationService);

    // Estado reativo com signals
    private readonly notificationsCache = signal<INotificationResponse[]>([]);
    private readonly currentNotification = signal<INotificationResponse | null>(null);
    private readonly unreadCount = signal<number>(0);

    // Computed signals públicos
    readonly notificationsListCache = computed(() => this.notificationsCache());
    readonly getCurrentNotification = computed(() => this.currentNotification());
    readonly getUnreadCount = computed(() => this.unreadCount());

    // EventEmitter para notificar componentes sobre novas notificações
    readonly onNewNotification = new EventEmitter<INotificationResponse>();

    // EventEmitter para mudança de status de panicEvent (vindo do socket)
    readonly onPanicStatusUpdated = new EventEmitter<{
        panicEventId: string;
        status: 'ATTENDED' | 'RESOLVED';
        updatedBy: string;
        updatedByName?: string;
    }>();

    // Permissões computadas (seguindo padrão do CompanyService)
    readonly canManageNotifications = computed(() => this.rolesService.isSystemAdmin());

    readonly canViewNotifications = computed(() => this.rolesService.isSystemAdmin());

    /**
     * Busca todas as notificações com paginação e filtros
     * @param filters Filtros opcionais de busca
     * @returns Observable com resposta paginada
     */
    buscarTodas(filters: INotificationFilters = {}): Observable<INotificationPaginationResponse> {
        return this.apiService
            .get<INotificationPaginationResponse>(NOTIFICATION_ENDPOINTS.BASE, {
                params: filters,
                useCache: false,
                cacheKey: NOTIFICATION_CACHE_KEYS.LIST(JSON.stringify(filters)),
                cacheTtl: 180000, // 3 minutos (dados mais dinâmicos)
            })
            .pipe(
                tap(response => {
                    // Atualizar cache local com novos dados
                    // A API retorna { notifications: [], total: number }
                    if (response.notifications && response.notifications.length > 0) {
                        this.notificationsCache.set(response.notifications);
                    }

                    // Atualizar contador de não lidas (calcular baseado nas notificações)
                    const unreadCount = response.notifications?.filter((n: any) => !n.isRead).length || 0;
                    this.unreadCount.set(unreadCount);
                }),
                catchError(error => {
                    console.error('Erro ao buscar notificações:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Busca notificações com filtro de pesquisa (busca no backend)
     * Quando há query, busca em TODA a base (sem limite)
     * @param query Termo de pesquisa
     * @param filters Filtros opcionais de busca
     * @returns Observable com resposta paginada
     */
    buscarNotificacoes(query: string, filters: INotificationFilters = {}): Observable<INotificationPaginationResponse> {
        // Construir params - não passar limit para buscar em toda a base
        const params: Record<string, any> = {
            q: query,
            page: filters.page || 1,
        };

        // Só adicionar filtros opcionais se fornecidos
        if (filters.isRead !== undefined) {
            params['isRead'] = filters.isRead;
        }
        if (filters.entityType) {
            params['entityType'] = filters.entityType;
        }

        return this.apiService
            .get<INotificationPaginationResponse>(NOTIFICATION_ENDPOINTS.SEARCH, {
                params,
                useCache: false,
            })
            .pipe(
                tap(response => {
                    // SEMPRE atualizar cache local com resultados da busca (inclusive vazio)
                    this.notificationsCache.set(response.notifications || []);
                }),
                catchError(error => {
                    console.error('Erro ao buscar notificações:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Busca notificação por ID específico
     * @param id ID da notificação
     * @returns Observable com dados da notificação
     */
    buscarPorId(id: string): Observable<INotificationResponse> {
        return this.apiService
            .get<INotificationResponse>(NOTIFICATION_ENDPOINTS.BY_ID(id), {
                useCache: true,
                cacheKey: NOTIFICATION_CACHE_KEYS.BY_ID(id),
                cacheTtl: 300000, // 5 minutos
            })
            .pipe(
                tap(notification => {
                    this.currentNotification.set(notification);
                    this.updateCacheWithNotification(notification);
                }),
                catchError(error => {
                    console.error(`Erro ao buscar notificação ${id}:`, error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Busca notificações não lidas
     * @param filters Filtros opcionais
     * @returns Observable com notificações não lidas
     */
    buscarNaoLidas(filters: INotificationFilters = {}): Observable<INotificationPaginationResponse> {
        return this.apiService
            .get<INotificationPaginationResponse>(NOTIFICATION_ENDPOINTS.UNREAD, {
                params: filters,
                useCache: true,
                cacheKey: NOTIFICATION_CACHE_KEYS.UNREAD(JSON.stringify(filters)),
                cacheTtl: 60000, // 1 minuto (dados muito dinâmicos)
            })
            .pipe(
                tap(response => {
                    // Atualizar contador de não lidas
                    this.unreadCount.set(response.unreadCount || 0);
                }),
                catchError(error => {
                    console.error('Erro ao buscar notificações não lidas:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Cria nova notificação
     * @param notificationData Dados da notificação
     * @returns Observable com notificação criada
     */
    criar(notificationData: ICreateNotificationDto): Observable<INotificationResponse> {
        return this.apiService
            .post<INotificationResponse>(NOTIFICATION_ENDPOINTS.BASE, notificationData, {
                useCache: false,
            })
            .pipe(
                tap(notification => {
                    // Adicionar ao cache local
                    this.updateCacheWithNotification(notification);

                    // Invalidar cache de listas
                    this.invalidateListCache();
                }),
                catchError(error => {
                    console.error('Erro ao criar notificação:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Atualiza notificação existente
     * @param id ID da notificação
     * @param updateData Dados para atualização
     * @returns Observable com notificação atualizada
     */
    atualizar(id: string, updateData: IUpdateNotificationDto): Observable<INotificationResponse> {
        return this.apiService
            .put<INotificationResponse>(NOTIFICATION_ENDPOINTS.BY_ID(id), updateData, {
                useCache: false,
            })
            .pipe(
                tap(notification => {
                    // Atualizar cache local
                    this.updateCacheWithNotification(notification);

                    // Se for a notificação atual, atualizar
                    if (this.currentNotification()?.id === id) {
                        this.currentNotification.set(notification);
                    }

                    // Invalidar cache de listas
                    this.invalidateListCache();
                }),
                catchError(error => {
                    console.error(`Erro ao atualizar notificação ${id}:`, error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Marca notificação como lida
     * @param id ID da notificação
     * @returns Observable com notificação atualizada
     */
    marcarComoLida(id: string): Observable<INotificationResponse> {
        return this.apiService
            .put<INotificationResponse>(
                NOTIFICATION_ENDPOINTS.MARK_AS_READ(id),
                {},
                {
                    useCache: false,
                }
            )
            .pipe(
                tap(notification => {
                    // Atualizar cache local
                    this.updateCacheWithNotification(notification);

                    // Se for a notificação atual, atualizar
                    if (this.currentNotification()?.id === id) {
                        this.currentNotification.set(notification);
                    }

                    // Invalidar cache de listas
                    this.invalidateListCache();
                }),
                catchError(error => {
                    console.error(`Erro ao marcar notificação ${id} como lida:`, error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Marca todas as notificações como lidas
     * @returns Observable com resultado da operação
     */
    marcarTodasComoLidas(): Observable<{ message: string; count: number }> {
        return this.apiService
            .put<{ message: string; count: number }>(
                NOTIFICATION_ENDPOINTS.MARK_ALL_AS_READ,
                {},
                {
                    useCache: false,
                }
            )
            .pipe(
                tap(result => {
                    // Atualizar contador de não lidas
                    this.unreadCount.set(0);

                    // Invalidar cache de listas
                    this.invalidateListCache();
                }),
                catchError(error => {
                    console.error('Erro ao marcar todas as notificações como lidas:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Remove notificação
     * @param id ID da notificação
     * @returns Observable com mensagem de confirmação
     */
    remover(id: string): Observable<{ message: string }> {
        return this.apiService
            .delete<{ message: string }>(NOTIFICATION_ENDPOINTS.BY_ID(id), {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Remover do cache local
                    this.removeFromCache(id);

                    // Se for a notificação atual, limpar
                    if (this.currentNotification()?.id === id) {
                        this.currentNotification.set(null);
                    }

                    // Invalidar cache de listas
                    this.invalidateListCache();
                }),
                catchError(error => {
                    console.error(`Erro ao remover notificação ${id}:`, error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Busca notificações por tipo
     * @param type Tipo da notificação
     * @param filters Filtros opcionais
     * @returns Observable com notificações do tipo
     */
    buscarPorTipo(type: NotificationType, filters: INotificationFilters = {}): Observable<INotificationPaginationResponse> {
        return this.apiService.get<INotificationPaginationResponse>(NOTIFICATION_ENDPOINTS.BY_TYPE(type), {
            params: filters,
            useCache: true,
            cacheKey: NOTIFICATION_CACHE_KEYS.BY_TYPE(type, JSON.stringify(filters)),
            cacheTtl: 180000, // 3 minutos
        });
    }

    /**
     * Busca notificações por prioridade
     * @param priority Prioridade da notificação
     * @param filters Filtros opcionais
     * @returns Observable com notificações da prioridade
     */
    buscarPorPrioridade(priority: NotificationPriority, filters: INotificationFilters = {}): Observable<INotificationPaginationResponse> {
        return this.apiService.get<INotificationPaginationResponse>(NOTIFICATION_ENDPOINTS.BY_PRIORITY(priority), {
            params: filters,
            useCache: true,
            cacheKey: NOTIFICATION_CACHE_KEYS.BY_PRIORITY(priority, JSON.stringify(filters)),
            cacheTtl: 180000, // 3 minutos
        });
    }

    /**
     * Obtém resumo das notificações
     * @returns Observable com resumo
     */
    obterResumo(): Observable<INotificationSummary> {
        return this.apiService
            .get<INotificationSummary>(NOTIFICATION_ENDPOINTS.SUMMARY, {
                useCache: true,
                cacheKey: NOTIFICATION_CACHE_KEYS.SUMMARY,
                cacheTtl: 300000, // 5 minutos
            })
            .pipe(
                catchError(error => {
                    console.error('Erro ao obter resumo das notificações:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Obtém estatísticas das notificações
     * @returns Observable com estatísticas
     */
    obterEstatisticas(): Observable<INotificationStats> {
        return this.apiService
            .get<INotificationStats>(NOTIFICATION_ENDPOINTS.STATS, {
                useCache: true,
                cacheKey: NOTIFICATION_CACHE_KEYS.STATS,
                cacheTtl: 300000, // 5 minutos
            })
            .pipe(
                catchError(error => {
                    console.error('Erro ao obter estatísticas das notificações:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Obtém configurações de notificação do usuário
     * @param userId ID do usuário
     * @returns Observable com configurações
     */
    obterConfiguracoes(userId: string): Observable<INotificationSettings> {
        return this.apiService
            .get<INotificationSettings>(NOTIFICATION_ENDPOINTS.SETTINGS(userId), {
                useCache: true,
                cacheKey: NOTIFICATION_CACHE_KEYS.SETTINGS(userId),
                cacheTtl: 600000, // 10 minutos
            })
            .pipe(
                catchError(error => {
                    console.error(`Erro ao obter configurações de notificação do usuário ${userId}:`, error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Atualiza configurações de notificação do usuário
     * @param userId ID do usuário
     * @param settings Configurações para atualizar
     * @returns Observable com configurações atualizadas
     */
    atualizarConfiguracoes(userId: string, settings: Partial<INotificationSettings>): Observable<INotificationSettings> {
        return this.apiService
            .put<INotificationSettings>(NOTIFICATION_ENDPOINTS.SETTINGS(userId), settings, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de configurações
                    this.apiService.clearCacheByKey(NOTIFICATION_CACHE_KEYS.SETTINGS(userId));
                }),
                catchError(error => {
                    console.error(`Erro ao atualizar configurações de notificação do usuário ${userId}:`, error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Executa ação em lote nas notificações
     * @param action Ação a ser executada
     * @returns Observable com resultado da operação
     */
    executarAcaoEmLote(action: IBulkNotificationAction): Observable<{ message: string; count: number }> {
        return this.apiService
            .post<{ message: string; count: number }>(NOTIFICATION_ENDPOINTS.BULK_ACTION, action, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de listas
                    this.invalidateListCache();
                }),
                catchError(error => {
                    console.error('Erro ao executar ação em lote nas notificações:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Busca templates de notificação
     * @returns Observable com templates
     */
    buscarTemplates(): Observable<INotificationTemplate[]> {
        return this.apiService
            .get<INotificationTemplate[]>(NOTIFICATION_ENDPOINTS.TEMPLATES, {
                useCache: true,
                cacheKey: NOTIFICATION_CACHE_KEYS.TEMPLATES,
                cacheTtl: 600000, // 10 minutos
            })
            .pipe(
                catchError(error => {
                    console.error('Erro ao buscar templates de notificação:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Atualiza contador de notificações não lidas
     * @returns Observable com contador atualizado
     */
    atualizarContadorNaoLidas(): Observable<number> {
        return this.apiService
            .get<number>(NOTIFICATION_ENDPOINTS.UNREAD_COUNT, {
                useCache: true,
                cacheKey: NOTIFICATION_CACHE_KEYS.UNREAD_COUNT,
                cacheTtl: 60000, // 1 minuto
            })
            .pipe(
                tap(count => {
                    this.unreadCount.set(count);
                }),
                catchError(error => {
                    console.error('Erro ao atualizar contador de notificações não lidas:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Limpa todos os caches do serviço
     */
    limparCaches(): void {
        this.apiService.clearCacheByPattern(NOTIFICATION_CACHE_KEYS.PATTERN);
        this.notificationsCache.set([]);
        this.currentNotification.set(null);
        this.unreadCount.set(0);
    }

    // ========================================
    // MÉTODOS PRIVADOS PARA GESTÃO DE CACHE
    // ========================================

    /**
     * Atualiza notificação no cache local
     */
    private updateCacheWithNotification(notification: INotificationResponse): void {
        const currentNotifications = this.notificationsCache();
        const index = currentNotifications.findIndex(n => n.id === notification.id);

        if (index !== -1) {
            // Atualizar notificação existente
            const updatedNotifications = [...currentNotifications];
            updatedNotifications[index] = notification;
            this.notificationsCache.set(updatedNotifications);
        } else {
            // Adicionar nova notificação
            this.notificationsCache.set([...currentNotifications, notification]);
        }
    }

    /**
     * Remove notificação do cache local
     */
    private removeFromCache(notificationId: string): void {
        const currentNotifications = this.notificationsCache();
        const filteredNotifications = currentNotifications.filter(n => n.id !== notificationId);
        this.notificationsCache.set(filteredNotifications);
    }

    /**
     * Atualiza visualmente o status de uma notificação de panicEvent.
     * Usado quando alguém resolve/atende via socket: a notificação continua
     * no cache (ainda não-lida do ponto de vista do backend) mas ganha um
     * marcador visual de "resolvida".
     */
    marcarPanicComoResolvido(
        panicEventId: string,
        status: 'ATTENDED' | 'RESOLVED',
        resolvedByName?: string,
    ): void {
        const current = this.notificationsCache();
        const updated = current.map(n =>
            n.entityType === 'panicEvent' && n.entityId === panicEventId
                ? {
                    ...n,
                    panicStatus: status,
                    panicResolvedBy: resolvedByName,
                }
                : n,
        );
        this.notificationsCache.set(updated);
    }

    /**
     * Invalida cache de listas
     */
    private invalidateListCache(): void {
        this.apiService.clearCacheByPattern(NOTIFICATION_CACHE_KEYS.PATTERN);
    }

    // ========================================
    // INTEGRAÇÃO COM SOCKET SERVICE
    // ========================================

    /**
     * Inicializa integração com socket para notificações em tempo real
     */
    ngOnInit(): void {
        // Aguardar socket conectar antes de registrar listeners
        const checkConnection = setInterval(() => {
            if (this.socketService.isConnected()) {
                console.log('🔌 Socket conectado, registrando listeners de notificação...');
                this.configurarSocketListeners();
                this.inicializarPushNotifications();
                clearInterval(checkConnection);
            }
        }, 100);

        // Timeout de segurança
        setTimeout(() => {
            clearInterval(checkConnection);
            if (!this.socketService.isConnected()) {
                console.warn('⚠️ Timeout: Socket não conectou, tentando registrar listeners mesmo assim...');
                this.configurarSocketListeners();
                this.inicializarPushNotifications();
            }
        }, 5000);
    }

    /**
     * Inicializa push notifications automaticamente se já tiver permissão
     */
    private async inicializarPushNotifications(): Promise<void> {
        try {
            console.log('[NotificationService] 🔔 Verificando permissão de notificações...');

            // Verificar se já tem permissão concedida
            const hasPermission = await this.pushNotificationService.hasPermission();
            console.log('[NotificationService] 📋 Tem permissão?', hasPermission);

            if (hasPermission) {
                console.log('[NotificationService] ✅ Permissão já concedida, inicializando...');
                const sucesso = await this.pushNotificationService.inicializar();
                console.log('[NotificationService] 📊 Resultado da inicialização:', sucesso);

                if (sucesso) {
                    console.log('[NotificationService] 🎉 Push notifications inicializadas automaticamente!');
                } else {
                    console.warn('[NotificationService] ⚠️ Falha ao inicializar push notifications');
                }
            } else {
                console.log('[NotificationService] ℹ️ Permissão ainda não concedida, aguardando usuário...');
            }
        } catch (error) {
            console.error('[NotificationService] ❌ Erro ao inicializar push notifications:', error);
        }
    }

    /**
     * Cleanup ao destruir o serviço
     */
    ngOnDestroy(): void {
        this.removerSocketListeners();
    }

    /**
     * Configura listeners do socket para notificações
     */
    private configurarSocketListeners(): void {
        // Escuta novas notificações (evento correto do backend)
        this.socketService.escutarEvento('new_notification', (notification: INotificationResponse) => {
            this.adicionarNotificacaoAoCache(notification);

            // Mostrar alerta visual
            this.mostrarAlertaNotificacao(notification);
        });

        // Escuta atualizações de notificações
        this.socketService.escutarEvento('notification_updated', (notification: INotificationResponse) => {
            this.atualizarNotificacaoNoCache(notification);
        });

        // Escuta exclusões de notificações
        this.socketService.escutarEvento('notification_deleted', (notificationId: string) => {
            this.removeFromCache(notificationId);
        });

        // Escuta atualização de contador de não lidas
        this.socketService.escutarEvento('unread_count_updated', (data: { unreadCount: number }) => {
            this.unreadCount.set(data.unreadCount);
        });

        // Escuta mudança de status de panicEvent (alguém atendeu/resolveu)
        this.socketService.escutarEvento('panic_status_updated', (payload: {
            panicEventId: string;
            status: 'ATTENDED' | 'RESOLVED';
            updatedBy: string;
            updatedByName?: string;
        }) => {
            this.onPanicStatusUpdated.emit(payload);
        });
    }

    /**
     * Remove listeners do socket
     */
    private removerSocketListeners(): void {
        this.socketService.pararEscuta('new_notification');
        this.socketService.pararEscuta('notification_updated');
        this.socketService.pararEscuta('notification_deleted');
        this.socketService.pararEscuta('unread_count_updated');
        this.socketService.pararEscuta('panic_status_updated');
    }

    /**
     * Adiciona nova notificação ao cache local
     */
    private adicionarNotificacaoAoCache(notification: INotificationResponse): void {
        const currentNotifications = this.notificationsCache();
        const updatedNotifications = [notification, ...currentNotifications];
        this.notificationsCache.set(updatedNotifications);

        // Atualizar contador de não lidas
        if (!notification.isRead) {
            this.unreadCount.update(count => count + 1);
        }
    }

    /**
     * Atualiza notificação existente no cache
     */
    private atualizarNotificacaoNoCache(notification: INotificationResponse): void {
        const currentNotifications = this.notificationsCache();
        const updatedNotifications = currentNotifications.map(n => (n.id === notification.id ? notification : n));
        this.notificationsCache.set(updatedNotifications);
    }

    /**
     * Mostra alerta visual para nova notificação
     */
    private mostrarAlertaNotificacao(notification: INotificationResponse): void {
        // Emitir evento para componentes reagirem
        this.onNewNotification.emit(notification);

        // No app nativo o SO já exibe push; aqui só atualizamos UI via socket
        if (typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.isNativePlatform?.()) {
            return;
        }

        // Push Notification do browser (web/PWA)
        if ('Notification' in window) {
            if (Notification.permission === 'granted') {
                this.mostrarPushNotification(notification);
            } else if (Notification.permission !== 'denied') {
                // Solicitar permissão se ainda não foi negada
                Notification.requestPermission().then(permission => {
                    if (permission === 'granted') {
                        this.mostrarPushNotification(notification);
                    }
                });
            }
        }
    }

    /**
     * Mostra push notification do browser
     */
    private mostrarPushNotification(notification: INotificationResponse): void {
        const pushNotification = new Notification(notification.title, {
            body: notification.message,
            icon: '/assets/icons/icon-192x192.png',
            badge: '/assets/icons/icon-96x96.png',
            tag: notification.id,
            requireInteraction: false, // Auto-fecha após alguns segundos
            silent: false, // Usa som padrão do sistema
        });

        // Ação ao clicar na notificação (opcional)
        pushNotification.onclick = () => {
            window.focus();
            pushNotification.close();

            // Aqui você pode navegar para a página relacionada
            // Exemplo: this.router.navigate(['/notifications', notification.id]);
        };
    }

    /**
     * Solicita permissão para push notifications
     * Pode ser chamado manualmente quando quiser pedir permissão ao usuário
     */
    solicitarPermissaoNotificacoes(): Promise<NotificationPermission> {
        if ('Notification' in window) {
            return Notification.requestPermission();
        }
        return Promise.reject('Notificações não suportadas');
    }
}
